import { z } from 'zod';
import { categoryCvxSchema, categorySchema, categoryStrictSchema } from './categorySchema';
import { menuItemCvxSchema, menuItemSchema, menuItemStrictSchema } from './menuItemSchema';
import {
  modifierGroupCvxSchema,
  modifierGroupSchema,
  modifierGroupStrictSchema,
} from './modifierGroupSchema';
import { modifierCvxSchema, modifierSchema, modifierStrictSchema } from './modifierSchema';

export const menuSchema = z.object({
  categories: z.array(categorySchema).default([]),
  menuItems: z.array(menuItemSchema).default([]),
  modifierGroups: z.array(modifierGroupSchema).default([]),
  modifiers: z.array(modifierSchema).default([]),
  restaurantId: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type Menu = z.output<typeof menuSchema>;

/** Strict schema */
export const menuStrictSchema = menuSchema
  // We use base schemas here. Validations are done in refine() steps below.
  // The only reason we extend here is to remove the default values.
  .safeExtend({
    categories: z.array(categorySchema),
    menuItems: z.array(menuItemSchema),
    modifierGroups: z.array(modifierGroupSchema),
    modifiers: z.array(modifierSchema),
  })
  /** Validate: Check if menu item => category references are valid */
  .refine(
    (menu) => {
      const categoryUuids = new Set(menu.categories.map((c) => c.uuid));

      // Check menuItems have valid links to categories
      const validCategories = menu.menuItems.every(
        (item) => !item.categoryUuid || categoryUuids.has(item.categoryUuid),
      );

      return validCategories;
    },
    {
      message: 'Invalid menu structure: Some menu items reference non-existent categories',
      path: ['menuItems', 'categoryUuid'],
    },
  )

  .superRefine((menu, ctx) => {
    const modifierGroupUuids = new Set(menu.modifierGroups.map((mg) => mg.uuid));
    const modifierUuids = new Set(menu.modifiers.map((m) => m.uuid));

    // Check menuItem.sizeModifierGroupUuid
    menu.menuItems.forEach((item) => {
      if (item.sizeModifierGroupUuid) {
        if (!modifierGroupUuids.has(item.sizeModifierGroupUuid)) {
          ctx.addIssue({
            code: 'custom',
            message: `Menu item "${item.name}" references non-existent size modifier group`,
            path: ['menuItems', item.uuid, 'sizeModifierGroupUuid'],
          });
        } else {
          const modifierGroup = menu.modifierGroups.find(
            (mg) => mg.uuid === item.sizeModifierGroupUuid,
          );
          if (modifierGroup?.modifierType !== 'SIZE') {
            ctx.addIssue({
              code: 'custom',
              message: `Menu item "${item.name}" references a non-SIZE modifier group for size options`,
              path: ['menuItems', item.uuid, 'sizeModifierGroupUuid'],
            });
          }
        }
      }

      // Check menuItem.modifierGroupUuids
      item.modifierGroupUuids.forEach((uuid) => {
        if (!modifierGroupUuids.has(uuid)) {
          ctx.addIssue({
            code: 'custom',
            message: `Menu item "${item.name}" references non-existent modifier group`,
            path: ['menuItems', item.uuid, 'modifierGroupUuids'],
          });
        }
      });
    });

    // Check modifierGroup.defaultModifierUuid
    menu.modifierGroups.forEach((mg) => {
      if (mg.defaultModifierUuid && !modifierUuids.has(mg.defaultModifierUuid)) {
        ctx.addIssue({
          code: 'custom',
          message: `Modifier group "${mg.name}" references non-existent default modifier`,
          path: ['modifierGroups', mg.uuid, 'defaultModifierUuid'],
        });
      }
    });
  })

  /** Validate: Linked modifier group must have at least one associated modifier */
  .superRefine((menu, ctx) => {
    const linkedModifierGroupUuids = new Set<string>();

    // Collect all linked modifier group UUIDs
    menu.menuItems.forEach((item) => {
      item.modifierGroupUuids.forEach((uuid) => linkedModifierGroupUuids.add(uuid));
      if (item.sizeModifierGroupUuid) {
        linkedModifierGroupUuids.add(item.sizeModifierGroupUuid);
      }
    });

    // Check if each linked modifier group has at least one associated modifier
    for (const groupUuid of linkedModifierGroupUuids) {
      const hasAssociatedModifier = menu.modifiers.some(
        (modifier) => modifier.modifierGroupUuid === groupUuid,
      );
      if (!hasAssociatedModifier) {
        ctx.addIssue({
          code: 'custom',
          message: `Modifier group ${groupUuid} must have at least one associated modifier`,
          path: ['modifierGroups', groupUuid],
        });
      }
    }
  })

  /** Validate: Menu items can have at most 3 "single choice" modifier groups and 2 "multi-choice" modifier groups */
  .refine(
    (menu) => {
      const modifierGroups = new Map(menu.modifierGroups.map((mg) => [mg.uuid, mg]));

      return menu.menuItems.every((item) => {
        const linkedGroups = item.modifierGroupUuids.map((uuid) => modifierGroups.get(uuid));
        const oneModifierGroups = linkedGroups.filter((mg) => mg?.qtyMax === 1);
        const multipleChoiceModifierGroups = linkedGroups.filter((mg) => mg && mg.qtyMax > 1);

        return oneModifierGroups.length <= 3 && multipleChoiceModifierGroups.length <= 2;
      });
    },
    {
      message:
        'Menu items can have at most 3 "single choice" modifier groups and 2 "multiple choice" modifier groups',
      path: ['menuItems', 'modifierGroupUuids'],
    },
  )

  /** Validate: all menu items */
  .superRefine((menu, ctx) => {
    menu.menuItems.forEach((menuItem) => {
      const validationResult = menuItemStrictSchema.safeDecode(menuItem);
      if (!validationResult.success) {
        const zodError = validationResult.error;
        zodError.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: `Menu item "${menuItem.name}" is invalid: ${issue.message}`,
            path: ['menuItems', menuItem.uuid, ...issue.path],
          });
        });
      }
    });
  })

  /** Validate: all categories */
  .superRefine((menu, ctx) => {
    menu.categories.forEach((category) => {
      const validationResult = categoryStrictSchema.safeDecode(category);
      if (!validationResult.success) {
        const zodError = validationResult.error;
        zodError.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: `Category "${category.name}" is invalid: ${issue.message}`,
            path: ['categories', category.uuid, ...issue.path],
          });
        });
      }
    });
  })

  /** Validate: all modifier groups */
  .superRefine((menu, ctx) => {
    menu.modifierGroups.forEach((group) => {
      const validationResult = modifierGroupStrictSchema.safeDecode(group);
      if (!validationResult.success) {
        const zodError = validationResult.error;
        zodError.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: `Modifier group "${group.name}" is invalid: ${issue.message}`,
            path: ['modifierGroups', group.uuid, ...issue.path],
          });
        });
      }
    });
  })

  /** Validate: all modifiers */
  .superRefine((menu, ctx) => {
    menu.modifiers.forEach((modifier) => {
      const validationResult = modifierStrictSchema.safeDecode(modifier);
      if (!validationResult.success) {
        const zodError = validationResult.error;
        zodError.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: `Modifier "${modifier.name}" is invalid: ${issue.message}`,
            path: ['modifiers', modifier.uuid, ...issue.path],
          });
        });
      }
    });
  })

  /** Validate: Ensure that direct mod groups are only associated to at most one menu item */
  .superRefine((menu, ctx) => {
    // mod group uuid / usage count (i.e. linked menu items count)
    const directModifierGroupUsage = new Map<string, number>();

    menu.menuItems.forEach((item) => {
      const allGroupUuids = [...item.modifierGroupUuids];
      if (item.sizeModifierGroupUuid) {
        allGroupUuids.push(item.sizeModifierGroupUuid);
      }

      allGroupUuids.forEach((groupUuid) => {
        const group = menu.modifierGroups.find((g) => g.uuid === groupUuid);
        if (group && !group.isShared) {
          directModifierGroupUsage.set(
            groupUuid,
            (directModifierGroupUsage.get(groupUuid) || 0) + 1,
          );
        }
      });
    });

    directModifierGroupUsage.forEach((count, groupUuid) => {
      if (count > 1) {
        const group = menu.modifierGroups.find((g) => g.uuid === groupUuid);
        if (group) {
          ctx.addIssue({
            code: 'custom',
            message: `Direct modifier group "${group.name}" can not be associated with more than one menu item`,
            path: ['modifierGroups', groupUuid],
          });
        }
      }
    });
  });

/** Convex schema */
export const menuCvxSchema = menuSchema.safeExtend({
  categories: z.array(categoryCvxSchema),
  menuItems: z.array(menuItemCvxSchema),
  modifierGroups: z.array(modifierGroupCvxSchema),
  modifiers: z.array(modifierCvxSchema),

  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type MenuCvx = z.output<typeof menuCvxSchema>;

export const menuWithoutRestaurantIdSchema = menuSchema.omit({ restaurantId: true });
export type MenuWithoutRestaurantId = z.output<typeof menuWithoutRestaurantIdSchema>;
